// The sentence box of the analyser: a large Devanagari textarea, example chips built by the engine and a Clear button.

import type { FC, ReactNode, RefObject } from 'react'

import { useId, useMemo } from 'react'
import { Eraser } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Bi } from '@/prakrit/components/common/bi'
import { MAX_CHARS, sameSentence } from '@/prakrit/components/grammar/vakya/vakya-parse'
import { VAKYA_EXAMPLES, exampleSentence, placeholderSentence } from '@/prakrit/components/grammar/vakya/vakya-examples'

interface VakyaInputProps {
    value: string
    onChange: (value: string) => void
    /** Put an example sentence in the box and analyse it at once. */
    onPick: (text: string) => void
    onClear: () => void
    /** The sentence being analysed, to mark the matching example chip. */
    active: string
    inputRef: RefObject<HTMLTextAreaElement | null>
}

export const VakyaInput: FC<VakyaInputProps> = ({ value, onChange, onPick, onClear, active, inputRef }): ReactNode => {
    const inputId = useId()
    const helpId = useId()
    const examples = useMemo(() => VAKYA_EXAMPLES.map(e => ({ ...e, text: exampleSentence(e) })), [])
    const placeholder = useMemo(() => placeholderSentence(), [])

    return (
        <section aria-label='वाक्य लिखें · Type a sentence' className='rounded-3xl border border-border bg-card p-4 shadow-card sm:p-6'>
            <label htmlFor={inputId} className='text-sm font-semibold'>
                <Bi hi='वाक्य' en='Sentence (in Devanagari)' layout='inline' always />
            </label>
            <textarea
                ref={inputRef}
                id={inputId}
                lang='pra'
                rows={3}
                value={value}
                maxLength={MAX_CHARS}
                autoComplete='off'
                autoCorrect='off'
                autoCapitalize='off'
                spellCheck={false}
                placeholder={placeholder}
                aria-describedby={helpId}
                onChange={event => onChange(event.target.value)}
                className='pra pra-lg mt-2 block min-h-28 w-full resize-y rounded-2xl border border-input bg-background/60 px-4 py-3 outline-none transition-colors placeholder:text-muted-foreground/60 hover:border-saffron/60 focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40'
            />
            <p id={helpId} className='mt-2 text-xs text-muted-foreground'>
                <Bi
                    hi='लिखते-लिखते विश्लेषण बदलता है। शब्दों के बीच खाली जगह छोड़िए।'
                    en='The analysis updates as you type. Leave a space between words.'
                    layout='inline'
                />
            </p>

            <div role='group' aria-label='उदाहरण वाक्य · Example sentences' className='mt-4 flex flex-wrap items-center gap-2'>
                {examples.map(e => {
                    const on = sameSentence(active, e.text)

                    return (
                        <button
                            key={e.id}
                            type='button'
                            aria-pressed={on}
                            onClick={() => onPick(e.text)}
                            className={cn(
                                'inline-flex min-h-tap flex-wrap items-center gap-x-2 gap-y-0.5 rounded-full border px-3.5 py-1 text-left transition-colors',
                                on ? 'border-foreground bg-foreground text-background' : 'border-border bg-card hover:border-saffron/60 hover:bg-saffron/5'
                            )}
                        >
                            <span lang='pra' className={cn('pra pra-sm leading-tight', on && 'text-background!')}>{e.text}</span>
                            <Bi
                                hi={e.label.hi}
                                en={e.label.en}
                                layout='inline'
                                className={cn('text-xs', on ? 'text-background/75' : 'text-muted-foreground')}
                            />
                        </button>
                    )
                })}
                <button
                    type='button'
                    onClick={onClear}
                    disabled={!value}
                    className='inline-flex min-h-tap items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-50'
                >
                    <Eraser className='size-4' aria-hidden='true' />
                    <Bi hi='मिटाएँ' en='Clear' layout='inline' always />
                </button>
            </div>
        </section>
    )
}
